import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { compressImage } from '../../../lib/imageCompression'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

export const useRecipes = () => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['recipes', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('recipes')
        .select('id, title, description, prep_time_minutes, cook_time_minutes, servings, ingredients, steps, rating, photo_url, source_url, created_by, created_at, updated_at')
        .eq('household_id', household.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: key })

  const uploadPhoto = async (file) => {
    const compressed = await compressImage(file, { maxWidthPx: 800, maxHeightPx: 800, quality: 0.85 })
    const path = `${household.id}/${crypto.randomUUID()}.jpg`
    const { error } = await supabase.storage.from('recipes').upload(path, compressed, { upsert: true })
    if (error) throw error
    const { data: { publicUrl } } = supabase.storage.from('recipes').getPublicUrl(path)
    return publicUrl
  }

  const createRecipe = async ({ photoFile, ...fields }) => {
    let photo_url = fields.photo_url ?? null
    if (photoFile) photo_url = await uploadPhoto(photoFile)
    const { data, error } = await supabase
      .from('recipes')
      .insert({ ...fields, photo_url, household_id: household.id, created_by: user.id })
      .select()
      .single()
    if (error) throw error
    invalidate()
    return data
  }

  const updateRecipe = async ({ id, photoFile, ...fields }) => {
    let photo_url = fields.photo_url
    if (photoFile) photo_url = await uploadPhoto(photoFile)
    const { error } = await supabase
      .from('recipes')
      .update({ ...fields, photo_url })
      .eq('id', id)
    if (error) throw error
    invalidate()
  }

  const deleteRecipe = async (id) => {
    const { error } = await supabase.from('recipes').delete().eq('id', id)
    if (error) throw error
    invalidate()
  }

  const scrapeRecipe = async (url) => {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(`${SUPABASE_URL}/functions/v1/scrape-recipe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ url }),
    })
    const body = await res.json()
    if (!res.ok) throw new Error(body.error ?? body.message ?? `Scrape failed (${res.status})`)
    return body
  }

  return { ...query, createRecipe, updateRecipe, deleteRecipe, scrapeRecipe, uploadPhoto }
}
